import humanAlone from "@/assets/tech-human-alone.jpg";
import robotAlone from "@/assets/tech-robot-alone.jpg";
import humanAi from "@/assets/tech-human-ai.jpg";

const panels = [
  {
    image: humanAlone,
    alt: "Overwhelmed worker buried in paperwork without technology",
    label: "Human Alone",
    desc: "Manual processes, paperwork overload, slow and error-prone.",
  },
  {
    image: robotAlone,
    alt: "Robot standing alone without human guidance",
    label: "Technology Alone",
    desc: "Capable but impersonal — no empathy, no human judgement.",
  },
  {
    image: humanAi,
    alt: "Professional empowered by AI technology working efficiently",
    label: "Human + AI",
    desc: "Skilled people enhanced by intelligent tools — faster, sharper, unstoppable.",
  },
];

export function TechExpertise() {
  return (
    <section className="py-14 md:py-20 bg-navy">
      <div className="container-main">
        {/* Heading & paragraph */}
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white uppercase leading-tight mb-4">
            Technology That Elevates Human Capability
          </h2>
          <p className="text-white/80 text-base leading-relaxed">
            At IntelliBPO, technology supports our people — it doesn't replace them. We integrate AI-enabled tools and intelligent automation to streamline repetitive processes, improve reporting and strengthen operational efficiency.
          </p>
        </div>

        {/* Three image panels with flow connectors */}
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {/* SVG flow connectors between panels - desktop only */}
          <svg
            className="hidden md:block absolute inset-0 w-full h-full pointer-events-none z-10"
            preserveAspectRatio="none"
            viewBox="0 0 1200 600"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Connector 1→2 */}
            <path
              d="M340,300 C400,300 400,240 430,240 C460,240 460,300 520,300"
              stroke="url(#flow1)"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
              className="animate-[dash_2s_ease-in-out_infinite]"
              strokeDasharray="8 6"
            />
            <circle cx="430" cy="240" r="4" fill="hsl(var(--secondary))" className="animate-pulse" />

            {/* Connector 2→3 */}
            <path
              d="M680,300 C740,300 740,240 770,240 C800,240 800,300 860,300"
              stroke="url(#flow2)"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
              className="animate-[dash_2s_ease-in-out_infinite_0.5s]"
              strokeDasharray="8 6"
            />
            <circle cx="770" cy="240" r="4" fill="hsl(var(--secondary))" className="animate-pulse" />

            {/* Arrow tip on connector 1 */}
            <polygon points="516,294 526,300 516,306" fill="hsl(var(--secondary))" opacity="0.8" />
            {/* Arrow tip on connector 2 */}
            <polygon points="856,294 866,300 856,306" fill="hsl(var(--secondary))" opacity="0.8" />

            <defs>
              <linearGradient id="flow1" x1="340" y1="300" x2="520" y2="300" gradientUnits="userSpaceOnUse">
                <stop stopColor="white" stopOpacity="0.3" />
                <stop offset="1" stopColor="hsl(var(--secondary))" stopOpacity="0.8" />
              </linearGradient>
              <linearGradient id="flow2" x1="680" y1="300" x2="860" y2="300" gradientUnits="userSpaceOnUse">
                <stop stopColor="hsl(var(--secondary))" stopOpacity="0.5" />
                <stop offset="1" stopColor="hsl(var(--secondary))" stopOpacity="1" />
              </linearGradient>
            </defs>
          </svg>

          {/* Mobile flow arrows */}
          {panels.map((panel, i) => (
            <div key={panel.label} className="relative">
              <div className="group text-center">
                <div className={`relative overflow-hidden rounded-xl border-2 ${
                  i === 2 ? "border-secondary shadow-lg shadow-secondary/20" : "border-white/10"
                }`}>
                  <img
                    src={panel.image}
                    alt={panel.alt}
                    loading="lazy"
                    width={640}
                    height={800}
                    className="w-full aspect-[4/5] object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {i === 2 && (
                    <div className="absolute inset-0 bg-secondary/10 pointer-events-none" />
                  )}
                </div>
                <h3 className={`mt-4 text-lg font-bold uppercase tracking-wide ${
                  i === 2 ? "text-secondary" : "text-white"
                }`}>
                  {panel.label}
                </h3>
                <p className="text-white/60 text-sm mt-1 max-w-xs mx-auto">
                  {panel.desc}
                </p>
              </div>

              {/* Mobile connector arrow */}
              {i < 2 && (
                <div className="flex justify-center my-4 md:hidden">
                  <svg width="24" height="40" viewBox="0 0 24 40" fill="none">
                    <path d="M12,0 L12,32" stroke="hsl(var(--secondary))" strokeWidth="2" strokeDasharray="4 4" />
                    <polygon points="6,30 12,40 18,30" fill="hsl(var(--secondary))" opacity="0.8" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
